import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AgentDetailView from './AgentDetailView';
import AddExistingDialog from './components/AddExistingDialog';
import AgentGrid from './components/AgentGrid';
import FilterBar from './components/FilterBar';
import { availableRuntimes } from './domain/filtering';
import FleetStrip from './components/FleetStrip';
import HeatmapPanel from './components/HeatmapPanel';
import KpiBar from './components/KpiBar';
import type { LoopState } from '../../apps/apis/commandCenter/types';
import { loopToCard, mergeLoopCards } from './domain/loopBoard';
import NodeRegistryPanel from './components/NodeRegistryPanel';
import SectionHeader from './components/SectionHeader';
import SectionTabs from './components/SectionTabs';
import ThemeToggle from './components/ThemeToggle';
import { getAgentDashboardToken } from '../../apps/apis/claudeCodeDashboard/instance';
import {
  useCatalog,
  useCommandCenterSummary,
  useCreateManagedHermesAgent,
  useCreateManagedN8nAgent,
  useDeleteManagedHermesAgent,
  useDeleteManagedN8nAgent,
  useFleetNodes,
  useCreateLoop,
  useDeleteLoop,
  useLoops,
  useTransitionLoop,
  useUpdateLoop,
  useHeatmap,
  useN8nLive,
  useNodeRegistry,
  useSkills,
  useRuns,
} from '../../apps/apis/commandCenter/hooks';
import { sectionCounts } from './domain/aggregate';
import { ROUTES } from '../../routes/routes';
import { owningNodeIdOf, slackAgentDmUrl } from './domain/slackAgent';
import {
  managedHermesCreateInput,
  managedN8nCreateInput,
  connectableHermesOptions,
  hermesSkillOptions,
  n8nWorkflowOptions,
  sanitizeAgentId,
  type HermesSkillOption,
  type N8nWorkflowOption,
} from './domain/addExisting';
import { cardsFromCatalog, codesFromCatalog, layoutFromCatalog, sectionsFromCatalog } from './domain/catalog';
import { heatByAgent, heatCellsFromRuns, heatmapHours, runStatsFromHeatmap } from './domain/heatmapData';
import { isUnregisteredCard, UNREGISTERED_SECTION, withUnregisteredCards } from './domain/unregistered';
import { catalogLabel, kpisWithN8n, liveDataNotice, liveRunRows, summaryFootnotes } from './domain/liveData';
import { ConfigReader } from '../../config/ConfigReader';
import {
  CommandCenterProvider,
  isSectionFolded,
  selectCode,
  selectGroups,
  selectTabs,
  useCommandCenter,
} from './state/commandCenterStore';
import { COMMAND_CENTER_CSS } from './styles';
import type { RuntimeFilter, SortMode, StatusFilter } from './types';

const HEATMAP_WINDOW_HOURS = 24;
/** A card totals a week of runs; the activity grid keeps its one-day window. */
const CARD_WINDOW_HOURS = 168;
/** Rows the open card's Recent runs table asks the server for. */
const DETAIL_RUN_LIMIT = 50;

/** Only used to label the axis before the heatmap answers; the response's own zone wins after that. */
const REPORTING_TIMEZONE = '+08:00';

const CommandCenter = () => {
  const { state, dispatch } = useCommandCenter();
  // Five independent queries: one failing resource must not blank out the others. In particular the
  // heatmap aggregate may be slow or down while the catalog below it still draws.
  const hasDashboardToken = getAgentDashboardToken() !== null;
  const catalogQuery = useCatalog(hasDashboardToken);
  const heatmapQuery = useHeatmap(HEATMAP_WINDOW_HOURS, hasDashboardToken);
  const cardHeatmapQuery = useHeatmap(CARD_WINDOW_HOURS, hasDashboardToken);
  const summaryQuery = useCommandCenterSummary(hasDashboardToken);
  const nodesQuery = useFleetNodes(hasDashboardToken);
  const runsQuery = useRuns({ limit: 200 }, hasDashboardToken);
  const n8nQuery = useN8nLive(hasDashboardToken);
  const notice = liveDataNotice({
    hasToken: hasDashboardToken,
    catalogError: catalogQuery.isError,
    heatmapError: heatmapQuery.isError || cardHeatmapQuery.isError,
    summaryError: summaryQuery.isError,
    nodesError: nodesQuery.isError,
    runsError: runsQuery.isError,
  });
  const registryQuery = useNodeRegistry(hasDashboardToken);
  const skillsQuery = useSkills(hasDashboardToken);
  const [registryOpen, setRegistryOpen] = useState(false);
  const loopsQuery = useLoops(hasDashboardToken);
  const createLoop = useCreateLoop();
  const transitionLoopMutation = useTransitionLoop();
  const deleteLoopMutation = useDeleteLoop();
  const updateLoopMutation = useUpdateLoop();
  // Which loop the skill picker is open for; dozens of skills need a search, not a dropdown on a tile.
  const [connectingLoopId, setConnectingLoopId] = useState<string | null>(null);
  const connectingLoop = useMemo(
    () => (loopsQuery.data?.loops ?? []).find((loop) => loop.loopId === connectingLoopId) ?? null,
    [loopsQuery.data, connectingLoopId],
  );
  // The section whose "+" was clicked — opens managed skill registration.
  const [addExistingSection, setAddExistingSection] = useState<string | null>(null);

  // n8n workflows and Hermes skills are no longer auto-dumped into one section; they are added one at a
  // time into a chosen domain section via the "+" picker, so the board renders the catalog as stored.
  const catalog = catalogQuery.data;

  const createManagedHermes = useCreateManagedHermesAgent();
  const createManagedN8n = useCreateManagedN8nAgent();
  const deleteManagedHermes = useDeleteManagedHermesAgent();
  const deleteManagedN8n = useDeleteManagedN8nAgent();
  const existingCardIds = useMemo(() => new Set((catalog?.agents ?? []).map((a) => a.id)), [catalog]);
  const hermesOptions = useMemo(
    () => hermesSkillOptions(skillsQuery.data?.skills ?? [], existingCardIds),
    [skillsQuery.data, existingCardIds],
  );
  // A card another loop already became is not offered again: one card cannot speak for two loops.
  const connectOptions = useMemo(
    () => connectableHermesOptions(hermesOptions, loopsQuery.data?.loops ?? [], connectingLoopId ?? undefined),
    [hermesOptions, loopsQuery.data, connectingLoopId],
  );
  const n8nOptions = useMemo(
    () => n8nWorkflowOptions(n8nQuery.data?.agents ?? [], existingCardIds),
    [n8nQuery.data?.agents, existingCardIds],
  );

  useEffect(() => {
    if (!catalog) {
      return;
    }
    dispatch({
      type: 'hydrateCatalog',
      sections: sectionsFromCatalog(catalog.sections),
      cards: cardsFromCatalog(catalog.agents),
      codes: codesFromCatalog(catalog.agents),
      layout: layoutFromCatalog(catalog.agents),
    });
  }, [catalog, dispatch]);

  // n8n executions live outside cc_runs, so the server heatmap never sees them. Fold them in client-side
  // (same as the runs list) so an n8n card lights up its activity row instead of showing an empty grid.
  const heatmap = useMemo(() => {
    const base = heatmapQuery.data;
    const timezone = base?.timezone ?? REPORTING_TIMEZONE;
    const windowHours = base?.windowHours ?? HEATMAP_WINDOW_HOURS;
    // A board cached before the server bucketed n8n runs has no heatmap; its recent runs are the fallback.
    const n8nCells =
      n8nQuery.data?.heatmap?.cells ?? heatCellsFromRuns(n8nQuery.data?.runs ?? [], timezone, windowHours);
    if (n8nCells.length === 0) {
      return base;
    }
    return { timezone, windowHours, cells: [...(base?.cells ?? []), ...n8nCells] };
  }, [heatmapQuery.data, n8nQuery.data]);
  // Card counters read a week: native runs from the 168h heatmap, n8n executions from the board's card window.
  const stats = useMemo(() => {
    const base = cardHeatmapQuery.data;
    const timezone = base?.timezone ?? REPORTING_TIMEZONE;
    const n8nCells =
      n8nQuery.data?.cardHeatmap?.cells ?? heatCellsFromRuns(n8nQuery.data?.runs ?? [], timezone, CARD_WINDOW_HOURS);
    return runStatsFromHeatmap({
      timezone,
      windowHours: CARD_WINDOW_HOURS,
      cells: [...(base?.cells ?? []), ...n8nCells],
    });
  }, [cardHeatmapQuery.data, n8nQuery.data]);
  // Skills that ran with no card join the board as a "미등록" section instead of vanishing from every count.
  const board = useMemo(() => withUnregisteredCards(state, stats), [state, stats]);
  const hours = useMemo(
    () => heatmapHours(heatmap?.timezone ?? REPORTING_TIMEZONE, heatmap?.windowHours ?? HEATMAP_WINDOW_HOURS),
    [heatmap],
  );
  const heat = useMemo(() => heatByAgent(heatmap, hours), [heatmap, hours]);

  const groups = useMemo(() => selectGroups(board, stats), [board, stats]);
  // Loops live in Spanner, not the catalog, so they join the board here rather than in the store —
  // in the grid AND in the flat list, which is what resolves the card the URL names.
  const loopCards = useMemo(() => (loopsQuery.data?.loops ?? []).map(loopToCard), [loopsQuery.data]);
  const groupsWithLoops = useMemo(
    () => mergeLoopCards(groups, loopsQuery.data?.loops ?? []),
    [groups, loopsQuery.data],
  );
  // The same cards the board draws, loops already merged in: looking the detail up in the pre-merge list
  // meant opening a wired loop lost the controls its own card was showing a moment earlier.
  const cards = useMemo(
    () => [...groupsWithLoops.flatMap((group) => group.cards), ...loopCards],
    [groupsWithLoops, loopCards],
  );
  const boardRuntimes = useMemo(() => availableRuntimes(cards), [cards]);
  const tabs = useMemo(() => selectTabs(board, stats), [board, stats]);
  const cardById = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);
  // The URL says which card is open. Keeping it in component state meant back left the board
  // entirely, a refresh lost the card, and no drill-down could be linked to.
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const detailId = cardId ?? null;
  const openDetail = useCallback((id: string) => navigate(ROUTES.ai.commandCenterAgent(id)), [navigate]);
  const closeDetail = useCallback(() => navigate(ROUTES.ai.commandCenter), [navigate]);
  const detailCard = detailId === null ? undefined : cardById.get(detailId);
  // The fleet list holds only the newest 200 runs (hours, not a day), so the open card asks for its own.
  const detailRunsQuery = useRuns(
    detailId === null ? { limit: DETAIL_RUN_LIMIT } : { agentId: detailId, limit: DETAIL_RUN_LIMIT },
    hasDashboardToken && detailId !== null,
  );
  // n8n executions folded into the run list newest-first; absent n8n data leaves it exactly as before.
  const mergedRuns = useMemo(() => {
    const base = detailRunsQuery.data?.runs ?? [];
    const n8nRuns = n8nQuery.data?.runs ?? [];
    if (n8nRuns.length === 0) {
      return base;
    }
    return [...base, ...n8nRuns].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [detailRunsQuery.data?.runs, n8nQuery.data?.runs]);
  const managedHermesSkillId = useMemo(() => {
    if (!detailCard || !detailCard.tags.some((tag) => tag.kind === 'hermes') || isUnregisteredCard(detailCard)) {
      return null;
    }
    return detailCard.id === sanitizeAgentId(`hermes-${detailCard.name}`) ? detailCard.name : null;
  }, [detailCard]);
  const managedN8nWorkflowId = useMemo(() => {
    if (!detailCard || !detailCard.tags.some((tag) => tag.kind === 'n8n') || !detailCard.id.startsWith('n8n-')) {
      return null;
    }
    const workflowId = detailCard.id.slice('n8n-'.length);
    return workflowId.length > 0 ? workflowId : null;
  }, [detailCard]);
  // A Hermes card's equivalent of the n8n editor is the agent's Slack conversation. The node is the one
  // that authored the skill, not whatever the card carries as owner; its display name is the identity
  // its Slack bot shares.
  const slackAgentUrl = useMemo(() => {
    if (!detailCard || !detailCard.tags.some((t) => t.kind === 'hermes')) {
      return null;
    }
    const nodeId = owningNodeIdOf(detailCard.id, skillsQuery.data?.skills ?? [], detailCard.owner);
    const node = nodesQuery.data?.nodes.find((n) => n.nodeId === nodeId);
    return slackAgentDmUrl(node?.displayName ?? null, ConfigReader.getInstance().getConfig().slackTeamId);
  }, [detailCard, nodesQuery.data, skillsQuery.data]);

  // n8n cards link straight to their workflow in n8n; the card id is `n8n-<workflowId>`.
  const n8nWorkflowUrl = useMemo(() => {
    if (!detailCard || !detailCard.tags.some((t) => t.kind === 'n8n')) {
      return null;
    }
    const workflowId = detailCard.id.replace(/^n8n-/, '');
    return `${ConfigReader.getInstance().getConfig().n8nWebhookBaseUrl}/workflow/${workflowId}`;
  }, [detailCard]);

  const say = useCallback((message: string) => dispatch({ type: 'toast', message }), [dispatch]);
  const codeOf = useCallback((id: string) => selectCode(state, id), [state]);
  const titleOf = useCallback(
    (slug: string) => board.sectionMeta[slug]?.title ?? board.sections.find((s) => s.slug === slug)?.title ?? slug,
    [board],
  );

  useEffect(() => {
    if (detailId === null) {
      return;
    }
    window.scrollTo({ top: 0 });
  }, [detailId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && detailId !== null) {
        closeDetail();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detailId, closeDetail]);

  const copyCode = useCallback(
    (code: string) => {
      void navigator.clipboard?.writeText(code);
      say(`Copied — ${code}`);
    },
    [say],
  );

  /** Registers the selected catalog skill as a live card for read-only monitoring. */
  const addHermes = (option: HermesSkillOption) => {
    if (addExistingSection === null || createManagedHermes.isPending) {
      return;
    }
    createManagedHermes.mutate(managedHermesCreateInput(option, addExistingSection), {
      onSuccess: (card) => say(`Added ${card.name} (${card.code})`),
      onError: () => say('Could not add — card was not changed'),
    });
  };

  /** Registers the selected live n8n workflow as a monitoring card only. */
  const addN8n = (option: N8nWorkflowOption) => {
    if (addExistingSection === null || createManagedN8n.isPending) {
      return;
    }
    createManagedN8n.mutate(managedN8nCreateInput(option, addExistingSection), {
      onSuccess: (card) => say(`Added ${card.name} (${card.code})`),
      onError: () => say('Could not add — card was not changed'),
    });
  };

  const moveLoop = (loopId: string, toState: LoopState) => {
    transitionLoopMutation.mutate(
      { loopId, toState },
      {
        onSuccess: (view) => say(`${view.name} → ${toState}`),
        onError: () => say(`Could not move to ${toState} — reload and try again`),
      },
    );
  };

  const removeLoop = (loopId: string) => {
    deleteLoopMutation.mutate(loopId, {
      onSuccess: () => say('Removed the plan'),
      onError: () => say('Could not remove — nothing was changed'),
    });
  };

  const unlinkLoop = (loopId: string) => {
    updateLoopMutation.mutate(
      { loopId, patch: { agentId: null } },
      {
        onSuccess: () => say('Unlinked — the plan is on a tile of its own again'),
        onError: () => say('Could not unlink — nothing was changed'),
      },
    );
  };

  /** Points a loop at the card it became; the tile then carries both the plan and the runs. */
  const linkLoop = (loopId: string, agentId: string) => {
    updateLoopMutation.mutate(
      { loopId, patch: { agentId } },
      {
        onSuccess: () => say('Connected — the plan and its runs now share a tile'),
        onError: () => say('Could not connect — nothing was changed'),
      },
    );
    setConnectingLoopId(null);
  };

  /** A skill with no card yet gets one first: there has to be a card for the runs to roll up to. */
  const connectHermes = (option: HermesSkillOption) => {
    if (!connectingLoop) {
      return;
    }
    if (option.onBoard) {
      linkLoop(connectingLoop.loopId, option.cardId);
      return;
    }
    createManagedHermes.mutate(managedHermesCreateInput(option, connectingLoop.section), {
      onSuccess: (card) => linkLoop(connectingLoop.loopId, card.id),
      onError: () => say('Could not connect — card was not created'),
    });
  };

  /** Removes only the selected managed card; the source skill remains available in the add dialog. */
  const removeManagedHermes = () => {
    if (managedHermesSkillId === null || deleteManagedHermes.isPending) {
      return;
    }
    deleteManagedHermes.mutate(managedHermesSkillId, {
      onSuccess: () => {
        closeDetail();
        say(`Removed ${managedHermesSkillId} from monitoring`);
      },
      onError: () => say('Could not remove — card was not changed'),
    });
  };

  /** Removes only the selected n8n monitoring card; workflow and executions remain in n8n. */
  const removeManagedN8n = () => {
    if (managedN8nWorkflowId === null || deleteManagedN8n.isPending) {
      return;
    }
    deleteManagedN8n.mutate(managedN8nWorkflowId, {
      onSuccess: () => {
        closeDetail();
        say(`Removed ${managedN8nWorkflowId} from monitoring`);
      },
      onError: () => say('Could not remove — card was not changed'),
    });
  };

  return (
    <div className={`cc-root${state.theme === 'dark' ? ' cc-dark' : ''}`}>
      <style>{COMMAND_CENTER_CSS}</style>

      {detailCard ? (
        <AgentDetailView
          card={detailCard}
          code={codeOf(detailCard.id)}
          liveRuns={liveRunRows(mergedRuns, detailCard.id)}
          n8nWorkflowUrl={n8nWorkflowUrl}
          slackAgentUrl={slackAgentUrl}
          skillId={managedHermesSkillId}
          metricOverride={state.overrides[detailCard.id]?.metric}
          nextOverride={state.overrides[detailCard.id]?.next}
          {...(managedHermesSkillId !== null
            ? { onRemoveManaged: removeManagedHermes }
            : managedN8nWorkflowId !== null
              ? { onRemoveManaged: removeManagedN8n }
              : {})}
          isRemovingManaged={deleteManagedHermes.isPending || deleteManagedN8n.isPending}
          onLoopTransition={moveLoop}
          onLoopRemove={removeLoop}
          onLoopConnect={setConnectingLoopId}
          onLoopDisconnect={unlinkLoop}
          onBack={closeDetail}
        />
      ) : (
        <section className="view on" id="view-dash">
          <h1>SKINSEOUL Agent Command Center</h1>

          <KpiBar
            kpis={kpisWithN8n(summaryQuery.data, n8nQuery.data?.runs ?? [])}
            footnotes={summaryFootnotes(summaryQuery.data)}
          />
          {notice ? (
            <div className={`cc-strip${notice.tone === 'warn' ? ' cc-strip-warn' : ''}`}>{notice.text}</div>
          ) : null}
          {hasDashboardToken ? <FleetStrip fleet={nodesQuery.data} isError={nodesQuery.isError} /> : null}

          {hasDashboardToken ? (
            <div className="cc-nodes-bar">
              <button type="button" className="btn" onClick={() => setRegistryOpen((open) => !open)}>
                Nodes
                {(registryQuery.data?.pending ?? 0) > 0 ? (
                  <span className="a-badge b-warn" data-testid="pending-nodes-badge">
                    {registryQuery.data?.pending} awaiting approval
                  </span>
                ) : null}
              </button>
            </div>
          ) : null}

          {registryOpen ? <NodeRegistryPanel registry={registryQuery.data} isError={registryQuery.isError} /> : null}

          <div className="sec-head">
            <h2>
              Agents{' '}
              <span className="cc-seed-tag">
                {catalogLabel({
                  hasToken: hasDashboardToken,
                  isError: catalogQuery.isError,
                  hydrated: state.hydrated,
                  total: state.catalogCards.length,
                })}
              </span>
            </h2>
          </div>

          <SectionTabs tabs={tabs} active={board.tab} onSelect={(tab) => dispatch({ type: 'setTab', tab })} />

          <FilterBar
            status={state.status}
            runtime={state.runtime}
            runtimes={boardRuntimes}
            sort={state.sort}
            onStatus={(status: StatusFilter) => dispatch({ type: 'setStatus', status })}
            onRuntime={(runtime: RuntimeFilter) => dispatch({ type: 'setRuntime', runtime })}
            onSort={(sort: SortMode) => dispatch({ type: 'setSort', sort })}
            onExpandAll={() => dispatch({ type: 'foldAll', folded: false })}
            onCollapseAll={() => dispatch({ type: 'foldAll', folded: true })}
          />

          {groupsWithLoops.map((group) => {
            const section = board.sections.find((s) => s.slug === group.slug);
            const folded = isSectionFolded(state, group.slug);
            return (
              <div
                key={group.slug}
                className={`fsec${folded ? ' collapsed' : ''}`}
                data-sec={group.slug}
                data-testid={`section-${group.slug}`}
              >
                <SectionHeader
                  title={titleOf(group.slug)}
                  desc={state.sectionMeta[group.slug]?.desc ?? section?.desc ?? ''}
                  counts={sectionCounts(group.cards)}
                  folded={folded}
                  onToggleFold={() => dispatch({ type: 'toggleFold', slug: group.slug })}
                  onAddManagedSkill={
                    group.slug === UNREGISTERED_SECTION ? undefined : () => setAddExistingSection(group.slug)
                  }
                />
                <AgentGrid
                  slug={group.slug}
                  cards={group.cards}
                  codeOf={codeOf}
                  onOpen={openDetail}
                  onCopyCode={copyCode}
                  onLoopTransition={moveLoop}
                  onLoopRemove={removeLoop}
                  onLoopConnect={(loopId) => setConnectingLoopId(loopId)}
                  onLoopDisconnect={unlinkLoop}
                />
              </div>
            );
          })}

          <HeatmapPanel
            groups={groups}
            hours={hours}
            heat={heat}
            timezone={heatmap?.timezone ?? REPORTING_TIMEZONE}
            isError={heatmapQuery.isError}
            titleOf={titleOf}
            codeOf={codeOf}
            onCopyCode={copyCode}
            onOpen={openDetail}
          />
        </section>
      )}

      <AddExistingDialog
        open={addExistingSection !== null}
        sectionTitle={addExistingSection !== null ? titleOf(addExistingSection) : ''}
        sectionSlug={addExistingSection ?? ''}
        hermesOptions={hermesOptions}
        n8nOptions={n8nOptions}
        n8nState={n8nQuery.isLoading ? 'loading' : n8nQuery.isError ? 'error' : 'ready'}
        isPending={createManagedHermes.isPending || createManagedN8n.isPending || createLoop.isPending}
        onCancel={() => setAddExistingSection(null)}
        onPickHermes={addHermes}
        onPickN8n={addN8n}
        onPlanLoop={(input) => {
          createLoop.mutate(input);
          setAddExistingSection(null);
        }}
      />
      <AddExistingDialog
        open={connectingLoop !== null}
        mode="connect"
        connectTargetName={connectingLoop?.name ?? ''}
        sectionTitle={connectingLoop ? titleOf(connectingLoop.section) : ''}
        sectionSlug={connectingLoop?.section ?? ''}
        hermesOptions={connectOptions}
        n8nOptions={[]}
        n8nState="ready"
        isPending={createManagedHermes.isPending || updateLoopMutation.isPending}
        onCancel={() => setConnectingLoopId(null)}
        onPickHermes={connectHermes}
        onPickN8n={() => undefined}
        onPlanLoop={() => undefined}
      />
      <ThemeToggle theme={state.theme} onChange={(theme) => dispatch({ type: 'setTheme', theme })} />
      {state.toast ? (
        <div className="toast" role="status">
          {state.toast.message}
        </div>
      ) : null}
    </div>
  );
};

/** Agent Command Center — dashboard and agent drill-down over the v0 dummy seed. */
const CommandCenterPage = () => (
  <CommandCenterProvider>
    <CommandCenter />
  </CommandCenterProvider>
);

export default CommandCenterPage;
